import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, BookOpen } from "lucide-react";
import { C, EASE, useContent } from "./theme";
import { SectionHead, splitHeading } from "./Chrome";
import { useIsDesktop } from "./hooks";

const SPINES = [
  { bg: "#F6D2BE", fg: C.burgundy },
  { bg: C.blushDeep, fg: C.wine },
  { bg: C.rose, fg: C.cream },
  { bg: C.goldLight, fg: C.wine },
  { bg: "#CFDCCB", fg: C.burgundy },
  { bg: C.gold, fg: C.wine },
  { bg: "#DCD3E8", fg: C.wine },
  { bg: C.blush, fg: C.burgundy },
  { bg: "#CFDDE8", fg: C.wine },
  { bg: "#EBC6CF", fg: C.burgundy },
];
const HEIGHTS = [330, 370, 310, 350, 385, 320, 360];
const WIDTHS = [62, 74, 58, 68, 64, 72, 60];

function Bookend({ letter }) {
  return (
    <div className="flex-shrink-0 w-11 h-28 lg:w-16 lg:h-40 flex items-end justify-center pb-3 rounded-t-full" style={{ background: C.paper, border: `1px solid ${C.gold}66` }} aria-hidden>
      <span className="ed-serif italic text-2xl lg:text-4xl" style={{ color: C.burgundy }}>{letter}</span>
    </div>
  );
}

export function ShelfSection({ articles }) {
  const c = useContent("writing");
  const contact = useContent("contact");
  const [hover, setHover] = useState(0);
  const desktop = useIsDesktop();
  const k = desktop ? 1 : 0.68;
  const [head, ital] = splitHeading(c.heading, "Browse the ideas,", "not the blog.");
  const a = articles[hover] || articles[0];
  return (
    <section id="writing" data-testid="bookshelf-writing" className="relative min-h-screen flex flex-col justify-center py-24 md:py-28" style={{ background: C.wine }}>
      <div className="max-w-[1440px] w-full mx-auto px-6 md:px-12">
        <SectionHead light num="05" eyebrow={c.eyebrow || "The Shelf · Writing"} heading={head} italic={ital} aside={c.subline || "A small library of essays on brand, search, and culture. Hover a spine for the gist — click to read."} />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            {!desktop && <p data-testid="bookshelf-swipe-hint" className="ed-mono text-[9px] uppercase tracking-[0.3em] text-right -mb-6" style={{ color: C.goldLight }}>Swipe for more →</p>}
            <div className="flex items-end gap-1.5 md:gap-2 px-2 md:px-4 overflow-x-auto pt-10 md:pt-12 [scrollbar-width:none]" data-testid="bookshelf">
              <Bookend letter="S" />
              {articles.map((ar, i) => {
                const s = SPINES[i % SPINES.length];
                const on = hover === i;
                return (
                  <motion.a
                    key={ar.id || i}
                    href={ar.href}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`book-${i}`}
                    onMouseEnter={() => setHover(i)}
                    onClick={(e) => { if (window.matchMedia("(hover: none)").matches && hover !== i) { e.preventDefault(); setHover(i); } }}
                    onFocus={() => setHover(i)}
                    animate={{ y: on ? -28 : 0, rotate: on ? -2 : 0 }}
                    transition={{ duration: 0.6, ease: EASE }}
                    className="relative flex-shrink-0 origin-bottom rounded-t-[3px]"
                    style={{ width: Math.round(WIDTHS[i % 7] * (desktop ? 1 : 0.78)), height: Math.round(HEIGHTS[i % 7] * k), background: s.bg, color: s.fg, boxShadow: `inset -8px 0 14px ${C.wine}26, inset 2px 0 0 ${C.cream}33`, border: s.bg === C.warm ? `1px solid ${C.gold}66` : "none" }}
                  >
                    <span className="absolute top-4 inset-x-3 h-px opacity-60" style={{ background: s.fg }} />
                    <span className="absolute top-6 inset-x-3 h-px opacity-60" style={{ background: s.fg }} />
                    <span className="absolute inset-0 flex items-center justify-center px-1.5 pt-7 pb-9 ed-serif text-[0.72rem] lg:text-[0.95rem] overflow-hidden leading-tight" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>
                      {ar.title}
                    </span>
                    <span className="absolute bottom-3 inset-x-0 text-center ed-mono text-[8px] opacity-70">{String(i + 1).padStart(2, "0")}</span>
                  </motion.a>
                );
              })}
              <Bookend letter="A" />
            </div>
            <div className="h-4 rounded-sm" style={{ background: C.gold, boxShadow: `0 2px 0 ${C.goldLight} inset` }} />
            <div className="h-2 mx-3" style={{ background: `${C.gold}55` }} />
          </div>
          <div className="lg:col-span-4">
            <AnimatePresence mode="wait">
              {a && (
                <motion.div key={hover} data-testid="book-preview" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.5, ease: EASE }} className="p-6 md:p-8" style={{ background: C.warm, border: `1px solid ${C.gold}55` }}>
                  <p className="ed-mono text-[10px] uppercase tracking-[0.3em]" style={{ color: C.gold }}>Vol. {String(hover + 1).padStart(2, "0")} · {a.date}</p>
                  <p className="ed-serif text-2xl md:text-3xl font-light leading-tight mt-3" style={{ color: C.wine }}>{a.title}</p>
                  <p className="ed-serif italic text-base mt-3" style={{ color: C.muted }}>{a.blurb}</p>
                  <a href={a.href} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-1.5 ed-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: C.rose }}>
                    Open the book <ArrowUpRight size={13} />
                  </a>
                </motion.div>
              )}
            </AnimatePresence>
            <a href={contact.medium || "https://medium.com/@sa9858"} target="_blank" rel="noreferrer" data-testid="bookshelf-all" className="mt-6 inline-flex items-center gap-2 ed-mono text-[10px] uppercase tracking-[0.25em] px-5 py-3 rounded-full transition-colors duration-500 hover:bg-[#58111A]" style={{ border: `1px solid ${C.cream}55`, color: C.cream }}>
              <BookOpen size={13} /> Every essay on Medium
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
